import { BehaviorSubject, Observable } from 'rxjs';
import { AzureOpenAI } from 'openai';
import { User, Alert, MessageEnteredEvent } from 'devextreme/ui/chat';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { AbcConfigService } from '@barthauer/abc/core/configuration';

export class AbcChatManager {
  private chatService: AzureOpenAI;
  private assistant: User = {
    id: 'assistant',
    name: 'Virtual Assistant',
  };
  private store: any[] = [];
  private messages: any[] = [];
  private customStore: CustomStore;
  private typingUsersSubject: BehaviorSubject<User[]> = new BehaviorSubject([]);
  private AzureOpenAIDemoConfig = {
    dangerouslyAllowBrowser: true,
    deployment: 'gpt-4o-mini',
    apiVersion: '2024-02-01',
    endpoint: 'https://public-api.devexpress.com/demo-openai',
    apiKey: 'DEMO',
  };
  public dataSource: DataSource;

  constructor(private configService: AbcConfigService) {
    this.initDataSource();
    this.typingUsersSubject.next([]);
    this.chatService = new AzureOpenAI(
      this.configService?.getConfiguration()?.AzureOpenAI ?? this.AzureOpenAIDemoConfig
    );
  }

  get typingUsers$(): Observable<User[]> {
    return this.typingUsersSubject.asObservable();
  }

  private initDataSource() {
    this.customStore = new CustomStore({
      key: 'id',
      load: () => {
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve([...this.store]);
          }, 0);
        });
      },
      insert: (message) => {
        return new Promise((resolve) => {
          setTimeout(() => {
            this.store.push(message);
            resolve(message);
          });
        });
      },
    });

    this.dataSource = new DataSource({
      store: this.customStore,
      paginate: false,
    });
  }

  private async getAIResponse(messages) {
    const params = {
      messages,
      model: this.AzureOpenAIDemoConfig.deployment,
      max_tokens: 1000,
      temperature: 0.7,
    };

    const response = await this.chatService.chat.completions.create(params);
    const data = { choices: response.choices };

    return data.choices[0].message?.content;
  }

  private async processMessageSending(message, event) {
    this.messages.push({ role: 'user', content: message.text });
    this.typingUsersSubject.next([this.assistant]);

    try {
      const aiResponse = await this.getAIResponse(this.messages);

      setTimeout(() => {
        this.typingUsersSubject.next([]);
        this.messages.push({ role: 'assistant', content: aiResponse });
        this.renderAssistantMessage(aiResponse);
      }, 200);
    } catch {
      this.typingUsersSubject.next([]);
      this.messages.pop();
    }
  }

  private renderAssistantMessage(text: string) {
    const message = {
      id: Date.now(),
      timestamp: new Date(),
      author: this.assistant,
      text,
    };

    this.dataSource.store().push([{ type: 'insert', data: message }]);
  }

  async onMessageEntered({ message, event }: MessageEnteredEvent) {
    this.dataSource.store().push([{ type: 'insert', data: { id: Date.now(), ...message } }]);
    await this.processMessageSending(message, event);
  }
}
